import React, { useEffect, useMemo, useState, useImperativeHandle } from "react";
import { Button, Card, Col, Row, Input, Switch, Spin } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { PATH } from "../../../redux/utils";
import { notify, TagsMultiSelect } from "../../ui";

const API_BASE = PATH.endsWith("/") ? PATH : `${PATH}/`;

const authHeader = () => {
  const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const collapseSpaces = (s) =>
  String(s ?? "")
    .normalize("NFC")
    .replace(/[ -]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const toNullIfEmpty = (v) => {
  const s = collapseSpaces(v);
  return s || null;
};

const onlyPhoneDigits = (v) => String(v || "").replace(/\D/g, "").slice(0, 15);

const normNombre = (s) =>
  String(s ?? "")
    .toUpperCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const emptyRazon = () => ({ nombre: "", tiene_corresponsal: false, corresponsales_ids: [] });

const FormEmpresas = React.forwardRef(
  ({ isEdit = false, initialValues = {}, onCancel, onSaved, onSubmit }, ref) => {
    const [submitting, setSubmitting] = useState(false);
    const [corresponsalesCatalog, setCorresponsalesCatalog] = useState([]);
    // Índice plano: { normNombre -> empresaNombre } para validar duplicados al guardar
    const [rsIndex, setRsIndex] = useState({});

    const [values, setValues] = useState({ nombre: "", correo: "", celular: "", razones_sociales: [emptyRazon()] });
    const [errors, setErrors] = useState({ nombre: "", razones: [] });

    // Cargar índice de razones sociales existentes (excluyendo la empresa actual en edición)
    useEffect(() => {
      const currentId = initialValues?.id_empresa ?? initialValues?.id ?? null;
      axios
        .get(`${API_BASE}empresas`, { headers: authHeader() })
        .then((r) => {
          const list = Array.isArray(r.data) ? r.data : Array.isArray(r.data?.items) ? r.data.items : [];
          const idx = {};
          for (const emp of list) {
            const empId = emp.id_empresa ?? emp.id ?? null;
            if (currentId && empId === currentId) continue; // excluir empresa actual
            const empNombre = emp.nombre || "";
            const rs = Array.isArray(emp.razones_sociales) ? emp.razones_sociales : [];
            for (const item of rs) {
              const nombre = item?.nombre ?? item?.name ?? (typeof item === "string" ? item : "");
              if (!nombre) continue;
              const key = normNombre(nombre);
              if (!idx[key]) idx[key] = empNombre;
            }
          }
          setRsIndex(idx);
        })
        .catch(() => {});
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValues?.id_empresa, initialValues?.id]);

    useEffect(() => {
      axios
        .get(`${API_BASE}empresas/corresponsales-catalog`, { headers: authHeader() })
        .then((r) => setCorresponsalesCatalog(r.data || []))
        .catch(() => {});
    }, []);

    const corresponsalesOptions = useMemo(
      () => corresponsalesCatalog.map((c) => ({ label: c.nombre, value: c.id_corresponsal })),
      [corresponsalesCatalog]
    );

    const mappedInitialValues = useMemo(() => {
      const src = initialValues || {};

      let razones = [];
      if (Array.isArray(src?.razones_sociales)) {
        razones = src.razones_sociales
          .map((r) => {
            const nombre = collapseSpaces(r?.nombre ?? r?.code ?? r ?? "");
            const corresponsales = Array.isArray(r?.corresponsales) ? r.corresponsales : [];
            const ids = corresponsales.map((c) => c.id || c.id_corresponsal).filter(Boolean);
            return { nombre, tiene_corresponsal: ids.length > 0, corresponsales_ids: ids };
          })
          .filter((x) => x.nombre);
      }

      if (!razones.length) razones = [emptyRazon()];

      return {
        nombre: src?.nombre ?? "",
        correo: src?.correo ?? "",
        celular: src?.celular ?? "",
        razones_sociales: razones,
      };
    }, [initialValues]);

    useEffect(() => {
      setValues(mappedInitialValues);
      setErrors({ nombre: "", razones: mappedInitialValues.razones_sociales.map(() => ({ nombre: "" })) });
    }, [mappedInitialValues]);

    const setField = (name, value) => setValues((v) => ({ ...v, [name]: value }));

    const setRazonField = (idx, patch) => {
      setValues((v) => {
        const razones = v.razones_sociales.slice();
        razones[idx] = { ...razones[idx], ...patch };
        return { ...v, razones_sociales: razones };
      });
    };

    const addRazon = () => {
      setValues((v) => ({ ...v, razones_sociales: [...v.razones_sociales, emptyRazon()] }));
      setErrors((e) => ({ ...e, razones: [...e.razones, { nombre: "" }] }));
    };

    const removeRazon = (idx) => {
      setValues((v) => ({ ...v, razones_sociales: v.razones_sociales.filter((_, i) => i !== idx) }));
      setErrors((e) => ({ ...e, razones: e.razones.filter((_, i) => i !== idx) }));
    };

    const buildPayload = () => {
      const nombre = collapseSpaces(values.nombre);
      if (!nombre) throw new Error("Nombre comercial requerido.");

      const rsClean = values.razones_sociales
        .map((r) => ({
          nombre: collapseSpaces(r?.nombre || ""),
          corresponsales_ids: r?.tiene_corresponsal
            ? Array.isArray(r?.corresponsales_ids) ? r.corresponsales_ids : []
            : [],
        }))
        .filter((r) => !!r.nombre);

      if (!rsClean.length) throw new Error("Debes capturar al menos una razón social.");

      return {
        nombre,
        correo: toNullIfEmpty(values.correo),
        celular: toNullIfEmpty(onlyPhoneDigits(values.celular)),
        razones_sociales: rsClean,
      };
    };

    // Devuelve true si es válido; si no, deja los errores cargados en el estado y hace scroll al primero.
    const validate = () => {
      const nextErrors = { nombre: "", razones: values.razones_sociales.map(() => ({ nombre: "" })) };
      let firstInvalidId = null;

      if (!collapseSpaces(values.nombre)) {
        nextErrors.nombre = "Requerido";
        firstInvalidId = firstInvalidId || "fe-nombre";
      }

      const seenInForm = new Map(); // normNombre -> primer índice
      values.razones_sociales.forEach((r, idx) => {
        const nombre = collapseSpaces(r?.nombre || "");
        if (!nombre) {
          nextErrors.razones[idx].nombre = "Requerido";
          firstInvalidId = firstInvalidId || `fe-razon-${idx}`;
          return;
        }
        const key = normNombre(nombre);
        if (seenInForm.has(key)) {
          const firstIdx = seenInForm.get(key);
          nextErrors.razones[firstIdx].nombre = "Razón social duplicada en este formulario";
          nextErrors.razones[idx].nombre = "Razón social duplicada en este formulario";
          firstInvalidId = firstInvalidId || `fe-razon-${firstIdx}`;
        } else {
          seenInForm.set(key, idx);
          const empresaNombre = rsIndex[key];
          if (empresaNombre) {
            nextErrors.razones[idx].nombre = `Ya existe en la empresa "${empresaNombre}"`;
            firstInvalidId = firstInvalidId || `fe-razon-${idx}`;
          }
        }
      });

      setErrors(nextErrors);

      const hasErrors =
        !!nextErrors.nombre || nextErrors.razones.some((r) => !!r.nombre);

      if (hasErrors && firstInvalidId) {
        document.getElementById(firstInvalidId)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      return !hasErrors;
    };

    const handleSave = async (e) => {
      e?.preventDefault?.();

      if (!validate()) {
        notify.error("Faltan campos por corregir", "Revisa los campos marcados en rojo antes de guardar.");
        return;
      }

      try {
        const payload = buildPayload();
        setSubmitting(true);

        if (typeof onSubmit === "function") {
          await onSubmit(payload);
        }

        notify.success(isEdit ? "Empresa actualizada" : "Empresa creada");
        onSaved?.(payload);
      } catch (err) {
        notify.error(err?.message || "No se pudo guardar la empresa.");
      } finally {
        setSubmitting(false);
      }
    };

    useImperativeHandle(ref, () => ({
      submit: () => handleSave(),
      validate: () => validate(),
      getValues: () => values,
    }));

    return (
      <div className="mx-auto" style={{ maxWidth: 980 }}>
        <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
          <h5 className="mb-0 text-uppercase">{isEdit ? "Editar · Empresa" : "Crear · Empresa"}</h5>
          <div className="d-flex gap-2">
            {onCancel ? (
              <Button onClick={onCancel}>
                Cancelar
              </Button>
            ) : null}
            <Button type="primary" onClick={handleSave} disabled={submitting}>
              {submitting && <Spin size="small" className="me-2" />}
              {isEdit ? "Guardar cambios" : "Guardar"}
            </Button>
          </div>
        </div>

        <form onSubmit={handleSave}>
          {/* ── Datos de la empresa ── */}
          <Card className="mb-3">
            <h6 className="text-uppercase text-secondary mb-3">Datos de la empresa</h6>
            <div className="mb-3" id="fe-nombre">
              <label className="d-block mb-1">Nombre comercial</label>
              <Input
                placeholder="Ej. Nombre Comercial"
                value={values.nombre}
                onChange={(e) => setField("nombre", e.target.value)}
                status={errors.nombre ? "error" : ""}
              />
              {errors.nombre && <div className="text-danger small mt-1">{errors.nombre}</div>}
            </div>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <div className="mb-3 mb-md-0" id="fe-correo">
                  <label className="d-block mb-1">Correo</label>
                  <Input
                    placeholder="correo@dominio.com"
                    value={values.correo}
                    onChange={(e) => setField("correo", e.target.value)}
                  />
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div id="fe-celular">
                  <label className="d-block mb-1">Celular</label>
                  <Input
                    placeholder="Ej. 6531234567"
                    value={values.celular}
                    onChange={(e) => setField("celular", onlyPhoneDigits(e.target.value))}
                  />
                </div>
              </Col>
            </Row>
          </Card>

          {/* ── Razones sociales ── */}
          <Card>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="text-uppercase text-secondary mb-0">Razones sociales</h6>
              <Button type="primary" ghost size="small" icon={<PlusOutlined />} onClick={addRazon}>
                Agregar
              </Button>
            </div>

            <div className="d-flex flex-column gap-3">
              {values.razones_sociales.map((r, idx) => (
                <Card key={idx} size="small">
                  <div className="d-flex gap-2 align-items-start">
                    <div className="flex-grow-1" id={`fe-razon-${idx}`}>
                      <Input
                        placeholder="Razón social (Ej. SA DE CV)"
                        value={r.nombre}
                        onChange={(e) => setRazonField(idx, { nombre: e.target.value })}
                        status={errors.razones[idx]?.nombre ? "error" : ""}
                      />
                      {errors.razones[idx]?.nombre && (
                        <div className="text-danger small mt-1">{errors.razones[idx]?.nombre}</div>
                      )}
                    </div>
                    <Button
                      danger
                      ghost
                      onClick={() => removeRazon(idx)}
                      disabled={values.razones_sociales.length <= 1}
                    >
                      Quitar
                    </Button>
                  </div>

                  <div className="d-flex align-items-center gap-2 mt-2">
                    <Switch
                      size="small"
                      checked={r.tiene_corresponsal}
                      onChange={(checked) => setRazonField(idx, { tiene_corresponsal: checked })}
                    />
                    <span>Tiene corresponsal</span>
                  </div>

                  {r.tiene_corresponsal && (
                    <div className="mt-2">
                      <small className="text-secondary d-block mb-1">Corresponsales</small>
                      <TagsMultiSelect
                        value={r.corresponsales_ids}
                        onChange={(ids) => setRazonField(idx, { corresponsales_ids: ids })}
                        options={corresponsalesOptions}
                        placeholder="— Seleccionar despacho —"
                      />
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </Card>
        </form>
      </div>
    );
  }
);

export default FormEmpresas;
